/**
 * SectionPanel — shown when a notebook folder/section is selected.
 * Lets students run AI operations across ALL notes in that section.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Zap, BookOpen, Target, List,
  ChevronDown, ChevronRight, Download, Share2,
  Loader, AlertCircle, CheckCircle, X,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function collectPagesFromFolder(folder, allNotebooks) {
  const pages = [];
  const walk = (items) => {
    if (!items) return;
    for (const item of items) {
      if (!item.isFolder) {
        const text = [item.title, ...(item.blocks || []).map(b => b.content)].filter(Boolean).join('\n');
        if (text.trim().length > 10) pages.push({ title: item.title || 'Untitled', text });
      }
      if (item.children) walk(item.children);
    }
  };
  walk(folder.children || []);
  return pages;
}

// ─── Operation config ────────────────────────────────────────────────────────
const OPERATIONS = [
  { id: 'test',           label: 'Practice Test',    icon: Target,   color: '#818cf8', desc: '10-12 MCQ questions from all your notes' },
  { id: 'flashcards',     label: 'Flashcard Deck',   icon: BookOpen, color: '#34d399', desc: '20-25 cards covering every major concept' },
  { id: 'study_guide',    label: 'Study Guide',      icon: FileText, color: '#c084fc', desc: 'Structured guide with summaries, terms, and exam predictions' },
  { id: 'talking_points', label: 'Talking Points',   icon: List,     color: '#fbbf24', desc: '10-15 presentation-ready talking points' },
  { id: 'all',            label: 'Generate All',     icon: Zap,      color: '#f97316', desc: 'Run all four operations at once' },
];

// ─── Flashcard viewer (inline) ────────────────────────────────────────────────
const InlineFlashcards = ({ cards }) => {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [mastery, setMastery] = useState({});
  const card = cards[idx];
  const known = Object.values(mastery).filter(v => v === 'know').length;

  const mark = (status) => {
    setMastery(m => ({ ...m, [idx]: status }));
    if (idx < cards.length - 1) { setIdx(i => i + 1); setFlipped(false); }
  };

  const exportCSV = () => {
    const rows = cards.map(c => `"${(c.front||'').replace(/"/g,'""')}","${(c.back||'').replace(/"/g,'""')}"`);
    const blob = new Blob(['Front,Back\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'flashcards.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
        <span>Card {idx + 1} / {cards.length} · {known} known</span>
        <button onClick={exportCSV} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '10px', padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Download size={10} /> Anki CSV
        </button>
      </div>
      <div
        onClick={() => setFlipped(f => !f)}
        style={{
          minHeight: '120px', borderRadius: '12px', padding: '16px',
          background: flipped ? 'rgba(52,211,153,0.07)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${flipped ? 'rgba(52,211,153,0.25)' : 'rgba(255,255,255,0.1)'}`,
          cursor: 'pointer', textAlign: 'center', fontSize: '13px', lineHeight: 1.6,
          color: 'rgba(255,255,255,0.9)', transition: 'all 0.2s', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: flipped ? 'rgba(52,211,153,0.7)' : 'rgba(255,255,255,0.3)', marginBottom: '8px' }}>
            {flipped ? 'Answer' : 'Tap to reveal'}
          </div>
          {flipped ? (card.back || card.answer) : (card.front || card.question)}
        </div>
      </div>
      {flipped ? (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => mark('learning')} style={{ flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)', color: '#f87171', cursor: 'pointer', fontWeight: 600, fontSize: '11.5px' }}>Still Learning</button>
          <button onClick={() => mark('know')} style={{ flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)', color: '#34d399', cursor: 'pointer', fontWeight: 600, fontSize: '11.5px' }}>Got It ✓</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button onClick={() => { setIdx(i => Math.max(0, i-1)); setFlipped(false); }} disabled={idx === 0}
            style={{ padding: '6px 14px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'rgba(255,255,255,0.5)', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1, fontSize: '11.5px' }}>← Prev</button>
          <button onClick={() => setFlipped(true)} style={{ padding: '6px 16px', borderRadius: '7px', border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.1)', color: '#818cf8', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600 }}>Reveal</button>
          <button onClick={() => { setIdx(i => Math.min(cards.length-1, i+1)); setFlipped(false); }} disabled={idx === cards.length - 1}
            style={{ padding: '6px 14px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'rgba(255,255,255,0.5)', cursor: idx === cards.length-1 ? 'not-allowed' : 'pointer', opacity: idx === cards.length-1 ? 0.4 : 1, fontSize: '11.5px' }}>Next →</button>
        </div>
      )}
    </div>
  );
};

// ─── Collapsible result section ───────────────────────────────────────────────
const ResultSection = ({ title, accent, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: '16px', border: `1px solid ${accent}25`, borderRadius: '12px', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '11px 14px', background: `${accent}10`, border: 'none',
          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 700, color: accent }}>{title}</span>
        {count !== undefined && <span style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>{count} items</span>}
        {open ? <ChevronDown size={13} color="rgba(255,255,255,0.4)" /> : <ChevronRight size={13} color="rgba(255,255,255,0.4)" />}
      </button>
      {open && <div style={{ padding: '14px' }}>{children}</div>}
    </div>
  );
};

// ─── Test question renderer ───────────────────────────────────────────────────
const TestViewer = ({ questions }) => {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState({});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {(questions || []).map((q, qi) => {
        const correct = (q.correctAnswers || q.answers || [])[0];
        const isChecked = checked[qi];
        return (
          <div key={qi} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: '10px' }}>
              Q{qi + 1}. {q.prompt || q.question}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {(q.choices || []).map((choice, ci) => {
                const selected = answers[qi] === ci;
                const isCorrect = ci === correct;
                let bg = 'rgba(255,255,255,0.04)', border = 'rgba(255,255,255,0.08)', color = 'rgba(255,255,255,0.7)';
                if (isChecked && isCorrect) { bg = 'rgba(52,211,153,0.1)'; border = 'rgba(52,211,153,0.3)'; color = '#34d399'; }
                else if (isChecked && selected && !isCorrect) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.3)'; color = '#f87171'; }
                else if (selected && !isChecked) { bg = 'rgba(99,102,241,0.1)'; border = 'rgba(99,102,241,0.3)'; color = '#818cf8'; }
                return (
                  <button key={ci} onClick={() => !isChecked && setAnswers(a => ({ ...a, [qi]: ci }))}
                    style={{ textAlign: 'left', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${border}`, background: bg, color, cursor: isChecked ? 'default' : 'pointer', fontSize: '12px', lineHeight: 1.5, transition: 'all 0.15s' }}>
                    <strong style={{ marginRight: '6px' }}>{['A','B','C','D'][ci]}.</strong>{choice}
                  </button>
                );
              })}
            </div>
            {!isChecked && answers[qi] !== undefined && (
              <button onClick={() => setChecked(c => ({ ...c, [qi]: true }))}
                style={{ marginTop: '8px', padding: '5px 12px', borderRadius: '6px', border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#818cf8', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600 }}>
                Check Answer
              </button>
            )}
            {isChecked && q.explanation && (
              <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '7px', borderLeft: '3px solid rgba(99,102,241,0.4)' }}>
                💡 {q.explanation}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Study guide renderer ──────────────────────────────────────────────────────
const StudyGuideViewer = ({ data }) => (
  <div>
    {data.overview && (
      <p style={{ fontSize: '13px', lineHeight: 1.6, color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>{data.overview}</p>
    )}
    {(data.sections || []).map((sec, i) => (
      <div key={i} style={{ marginBottom: '14px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#c084fc', marginBottom: '6px' }}>{sec.heading}</div>
        <p style={{ fontSize: '12.5px', lineHeight: 1.6, color: 'rgba(255,255,255,0.75)', margin: '0 0 8px' }}>{sec.summary}</p>
        {(sec.key_terms || []).length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            {sec.key_terms.map((t, ti) => (
              <div key={ti} style={{ fontSize: '11.5px', marginBottom: '4px' }}>
                <span style={{ color: '#c084fc', fontWeight: 700 }}>{t.term}:</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', marginLeft: '6px' }}>{t.definition}</span>
              </div>
            ))}
          </div>
        )}
        {(sec.key_facts || []).length > 0 && (
          <div>
            {sec.key_facts.map((f, fi) => (
              <div key={fi} style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.6)', display: 'flex', gap: '6px', marginBottom: '3px' }}>
                <span style={{ color: '#818cf8' }}>→</span>{f}
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
    {(data.exam_predictions || []).length > 0 && (
      <div style={{ padding: '12px', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '10px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', marginBottom: '6px' }}>💡 Likely Exam Topics</div>
        {data.exam_predictions.map((p, i) => (
          <div key={i} style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.65)', marginBottom: '3px' }}>• {p}</div>
        ))}
      </div>
    )}
  </div>
);

// ─── Talking points renderer ──────────────────────────────────────────────────
const TalkingPointsViewer = ({ points }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {(points || []).map((pt, i) => {
      const imp = pt.importance || 'medium';
      const impColor = imp === 'high' ? '#f87171' : imp === 'medium' ? '#fbbf24' : '#6b7280';
      return (
        <div key={i} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', borderLeft: `3px solid ${impColor}` }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: '5px' }}>
            <span style={{ fontSize: '9px', textTransform: 'uppercase', color: impColor, fontWeight: 700, marginRight: '8px', letterSpacing: '0.06em' }}>{imp}</span>
            {pt.point}
          </div>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, margin: '0 0 4px' }}>{pt.detail}</p>
          {pt.example && (
            <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>e.g. {pt.example}</div>
          )}
        </div>
      );
    })}
  </div>
);

// ─── Main SectionPanel ────────────────────────────────────────────────────────
const SectionPanel = ({ folder, notebooks, onOpenPage, onGenerateKnowledgeGraph }) => {
  const pages = useMemo(() => collectPagesFromFolder(folder, notebooks), [folder, notebooks]);
  const [loading, setLoading] = useState(false);
  const [activeOp, setActiveOp] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('test');

  const totalWords = useMemo(
    () => pages.reduce((acc, p) => acc + p.text.split(/\s+/).length, 0),
    [pages]
  );

  const handleRun = useCallback(async (opId) => {
    if (pages.length === 0) { setError('This section has no pages with content yet.'); return; }
    setLoading(true);
    setActiveOp(opId);
    setError('');
    setResults(null);

    try {
      const res = await fetch(`${API_BASE}/api/section_study`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_title: folder.name,
          pages,
          operation: opId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${res.status}`);
      }
      const data = await res.json();
      setResults(data);
      setActiveTab(opId === 'all' ? 'test' : opId);
    } catch (e) {
      setError(e.message || 'Generation failed. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [pages, folder.name]);

  const handleKnowledgeGraph = useCallback(async () => {
    if (pages.length === 0) return;
    const combinedText = pages.map(p => `=== ${p.title} ===\n${p.text}`).join('\n\n');
    onGenerateKnowledgeGraph(combinedText, folder.name);
  }, [pages, folder.name, onGenerateKnowledgeGraph]);

  const allChildren = folder.children || [];
  const directPages = allChildren.filter(c => !c.isFolder);
  const subFolders  = allChildren.filter(c => c.isFolder);

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '28px 32px 48px' }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={18} color="#c084fc" strokeWidth={1.7} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{folder.name}</h1>
            <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
              {pages.length} page{pages.length !== 1 ? 's' : ''} · {totalWords.toLocaleString()} words
            </p>
          </div>
        </div>
      </div>

      {/* Pages in this section */}
      {(directPages.length > 0 || subFolders.length > 0) && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.3)', marginBottom: '10px' }}>Pages in this section</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
            {directPages.map(p => (
              <button key={p.id} onClick={() => onOpenPage(p)}
                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: '9px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '7px', transition: 'all 0.15s' }}>
                <FileText size={13} color="rgba(255,255,255,0.4)" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || 'Untitled'}</span>
              </button>
            ))}
            {subFolders.map(f => (
              <div key={f.id}
                style={{ padding: '10px 12px', borderRadius: '9px', border: '1px solid rgba(139,92,246,0.15)', background: 'rgba(139,92,246,0.05)', color: 'rgba(255,255,255,0.5)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                <BookOpen size={13} color="#c084fc" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Operations */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' }}>
          AI Operations — across all {pages.length} pages
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
          {OPERATIONS.map(op => {
            const Icon = op.icon;
            const isRunning = loading && activeOp === op.id;
            return (
              <motion.button
                key={op.id}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => handleRun(op.id)}
                disabled={loading || pages.length === 0}
                style={{
                  padding: '14px', borderRadius: '12px', border: `1px solid ${op.color}30`,
                  background: `${op.color}0f`, cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading && activeOp !== op.id ? 0.5 : 1,
                  textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '6px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  {isRunning
                    ? <Loader size={15} color={op.color} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Icon size={15} color={op.color} strokeWidth={1.8} />}
                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: op.color }}>{op.label}</span>
                </div>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>{op.desc}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Knowledge Graph button */}
        <button
          onClick={handleKnowledgeGraph}
          disabled={pages.length === 0}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: '12px',
            border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.08)',
            color: '#818cf8', cursor: pages.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '12.5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px',
            opacity: pages.length === 0 ? 0.4 : 1,
          }}
        >
          <Share2 size={14} strokeWidth={1.8} />
          Generate Knowledge Graph → Canvas
          <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>Opens in Study Canvas</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontSize: '12.5px', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Results */}
      <AnimatePresence>
        {results && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {/* Tab strip */}
            {['test', 'flashcards', 'study_guide', 'talking_points'].some(k => results[k]) && (
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { id: 'test', label: 'Practice Test', show: !!results.test?.questions?.length },
                  { id: 'flashcards', label: 'Flashcards', show: !!results.flashcards?.cards?.length },
                  { id: 'study_guide', label: 'Study Guide', show: !!results.study_guide?.sections?.length },
                  { id: 'talking_points', label: 'Talking Points', show: Array.isArray(results.talking_points) && results.talking_points.length > 0 },
                ].filter(t => t.show).map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', border: 'none', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
                      background: activeTab === t.id ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)',
                      color: activeTab === t.id ? '#c084fc' : 'rgba(255,255,255,0.4)',
                      borderBottom: activeTab === t.id ? '2px solid #9333ea' : '2px solid transparent',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'test' && results.test?.questions?.length > 0 && (
              <ResultSection title="Practice Test" accent="#818cf8" count={results.test.questions.length}>
                <TestViewer questions={results.test.questions} />
              </ResultSection>
            )}
            {activeTab === 'flashcards' && results.flashcards?.cards?.length > 0 && (
              <ResultSection title="Flashcard Deck" accent="#34d399" count={results.flashcards.cards.length}>
                <InlineFlashcards cards={results.flashcards.cards} />
              </ResultSection>
            )}
            {activeTab === 'study_guide' && results.study_guide?.sections?.length > 0 && (
              <ResultSection title="Study Guide" accent="#c084fc">
                <StudyGuideViewer data={results.study_guide} />
              </ResultSection>
            )}
            {activeTab === 'talking_points' && Array.isArray(results.talking_points) && results.talking_points.length > 0 && (
              <ResultSection title="Talking Points" accent="#fbbf24" count={results.talking_points.length}>
                <TalkingPointsViewer points={results.talking_points} />
              </ResultSection>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state */}
      {pages.length === 0 && (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
          <BookOpen size={32} strokeWidth={1.2} style={{ marginBottom: '12px', opacity: 0.4 }} />
          <p style={{ fontSize: '13px', margin: 0 }}>This section has no pages yet.</p>
          <p style={{ fontSize: '12px', margin: '6px 0 0', color: 'rgba(255,255,255,0.2)' }}>Create pages inside this folder to enable AI operations.</p>
        </div>
      )}
    </div>
  );
};

export default SectionPanel;
